import { endedByDailyCutoff } from '@proerp/shared';
/**
 * Sitzung im Partnerportal (ADR 0085). Ablauf und Token sind dieselben wie
 * intern (ADR 0012/0034) — nur der Speicherschlüssel ist ein anderer.
 *
 * Das ist kein Detail: In Produktion trennt schon die Domain
 * (partner.probig.com vs. erp.probig.com) den localStorage. Lokal laufen aber
 * beide Apps auf `localhost` und teilen ihn sich — mit gleichem Schlüssel
 * würde eine Anmeldung die andere überschreiben und man säße vor
 * unerklärlichen 403ern.
 */
const TOKEN_KEY = 'proerp.portal.accessToken';

/**
 * Warum die letzte Sitzung endete — überlebt den Neuladen-Sprung zur Anmeldung.
 * `sessionStorage`, nicht `localStorage`: Der Hinweis gehört zu diesem Tab.
 */
const END_REASON_KEY = 'proerp.portal.sessionEndReason';

/**
 * Warum die Sitzung endete. `idle` = 12 h Ruhe (ADR 0106) · `daily` =
 * nächtlicher Schnitt um 3 Uhr österreichischer Zeit (ADR 0107).
 */
export type SessionEndReason = 'idle' | 'daily';

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Privater Modus o. Ä. — die Sitzung gilt dann nur für diesen Tab.
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // s. o.
  }
}

/** Abmelden: Token verwerfen und hart neu laden (verwirft allen Zustand). */
export function logout(): void {
  clearToken();
  window.location.assign('/');
}

/**
 * Sitzung beenden, weil sie vorbei ist — nicht, weil jemand auf „Abmelden"
 * geklickt hat (ADR 0106). Merkt den Grund, damit die Anmeldeseite ihn nennen
 * kann; ohne Token passiert nichts, damit paralleles Auslösen die Seite nicht
 * mehrfach neu lädt.
 */
export function endSession(reason: SessionEndReason): void {
  if (!getStoredToken()) return;
  clearToken();
  try {
    sessionStorage.setItem(END_REASON_KEY, reason);
  } catch {
    // Ohne sessionStorage fehlt nur der Hinweistext.
  }
  window.location.assign('/');
}

/**
 * Wie im ERP je Seitenaufbau gemerkt statt verbraucht — React ruft im
 * StrictMode auch `useState`-Initialisierer doppelt auf (siehe
 * `apps/web/src/lib/auth.ts`).
 */
let rememberedReason: SessionEndReason | null | undefined;

export function takeSessionEndReason(): SessionEndReason | null {
  if (rememberedReason === undefined) {
    try {
      const reason = sessionStorage.getItem(END_REASON_KEY);
      if (reason) sessionStorage.removeItem(END_REASON_KEY);
      rememberedReason = reason === 'idle' || reason === 'daily' ? reason : null;
    } catch {
      rememberedReason = null;
    }
  }
  return rememberedReason;
}

/**
 * Grund aus dem gespeicherten Token ableiten: Wurde es auf weniger als das
 * volle Leerlauf-Fenster ausgestellt, hat der nächtliche Schnitt es gekürzt.
 */
export function currentEndReason(): SessionEndReason {
  return endedByDailyCutoff(getStoredToken()) ? 'daily' : 'idle';
}

/** Freundlicher Geräte-Anzeigename für einen neu registrierten Passkey (ADR 0034). */
export function guessDeviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Android/.test(ua)) return 'Android-Gerät';
  if (/Windows/.test(ua)) return 'Windows-PC';
  return 'Mein Gerät';
}
